import React from "react";
import axios from "axios";
import toastr from "toastr";
import { Spinner, Button } from "react-bootstrap";
import { CustomDateRangePicker } from "components/CustomDateRangePicker";
import { CSVLink } from "react-csv";
import CustomToolTip from "components/CustomToolTip";

interface MetricBundle {
    advisorsFirstPolicy: number;
    policies: number;
    claimsProcessedCount: number;
    claimsProcessedAmount: number;
    fundedWithin30Days: { numerator: number; denominator: number; percent: number | null };
    slaWithin3BusinessDays: { numerator: number; denominator: number; percent: number | null };
    errors?: Record<string, string> | null;
}

interface ResponseData {
    success: boolean;
    dateFrom: string;
    dateTo: string;
    jgOnly: boolean;
    current: MetricBundle;
}

export default class LeadershipMetricsReport extends React.Component<any, any> {
    state = {
        fromDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        toDate: new Date(),
        jgOnly: false,
        loading: true,
        error: null as string | null,
        data: null as ResponseData | null,
        contestAdvisors: [] as any[],
    };

    componentDidMount() {
        this.load();
        this.loadContestAdvisors();
    }

    loadContestAdvisors = () => {
        axios
            .get("/api/opsreport/getcontestadvisorsummary")
            .then((res) => {
                this.setState({ contestAdvisors: res.data.summary || [] });
            })
            .catch(() => {});
    };

    componentDidUpdate(_prev: any, prevState: any) {
        if (prevState.jgOnly !== this.state.jgOnly) {
            this.load();
        }
    }

    load = () => {
        this.setState({ loading: true, error: null });
        const params = {
            dateFrom: toIsoDate(this.state.fromDate),
            dateTo:   toIsoDate(this.state.toDate),
            jgOnly:   this.state.jgOnly,
        };
        axios
            .get("/api/LeadershipMetrics/getleadershipmetrics", { params })
            .then((res) => {
                if (!res.data.success) {
                    const msg = res.data.message || "Failed to load metrics";
                    this.setState({ loading: false, error: msg });
                    toastr.error(msg, "Error");
                    return;
                }
                this.setState({ loading: false, data: res.data });
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || err.message || "Network error";
                this.setState({ loading: false, error: msg });
                toastr.error(msg, "Error");
            });
    };

    render() {
        const { loading, error, data, fromDate, toDate, jgOnly } = this.state;
        return (
            <div className="leadership-metrics-report">
                <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                    <span className="text-muted small">Date range:</span>
                    <CustomDateRangePicker
                        fromDate={fromDate}
                        toDate={toDate}
                        setFromDate={(d: Date) => this.setState({ fromDate: d })}
                        setToDate={(d: Date) => this.setState({ toDate: d })}
                    />
                    <Button variant="primary" size="sm" onClick={this.load}>
                        Apply
                    </Button>
                    <span className="text-muted small ml-3">Audience:</span>
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className={`btn btn-sm ${!jgOnly ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => this.setState({ jgOnly: false })}
                        >
                            NHC (All)
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${ jgOnly ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => this.setState({ jgOnly: true })}
                        >
                            JG only
                        </button>
                    </div>
                    {data && (
                        <CSVLink
                            data={[{
                                DateRange: `${data.dateFrom} to ${data.dateTo}`,
                                Audience: jgOnly ? "JG only" : "NHC (All)",
                                AdvisorsFirstEnroll: data.current.advisorsFirstPolicy,
                                "Enrollment Volume": data.current.policies,
                                ClaimsCount: data.current.claimsProcessedCount,
                                ClaimsAmount: data.current.claimsProcessedAmount,
                                PercentFunded30d: data.current.fundedWithin30Days.percent ?? "",
                                PercentSla3d: data.current.slaWithin3BusinessDays.percent ?? "",
                            }]}
                            filename={`leadership-metrics-${data.dateFrom}-to-${data.dateTo}.csv`}
                            className="btn btn-sm btn-outline-secondary ml-auto"
                        >
                            Export CSV
                        </CSVLink>
                    )}
                </div>

                {loading && (
                    <div className="text-center p-5"><Spinner animation="border" /></div>
                )}
                {!loading && error && (
                    <div className="alert alert-danger">Failed to load metrics: {error}</div>
                )}
                {!loading && !error && data && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                        <MetricTile
                            label="Advisors w/ first policy"
                            value={formatNumber(data.current.advisorsFirstPolicy)}
                            infoHeader="Advisors w/ first policy"
                            infoBody="Counts advisors who landed their very first policy during this period. Only includes advisors who joined the team in 2026 or later."
                            errorMsg={data.current.errors?.advisorsFirstPolicy}
                        />
                        <MetricTile
                            label="Enrollment Volume"
                            value={formatNumber(data.current.policies)}
                            infoHeader="Enrollment Volume"
                            infoBody="Policies created in this period (by creation date), including cancelled and not-yet-activated policies."
                            errorMsg={data.current.errors?.policies}
                        />
                        <MetricTile
                            label="# Claims processed"
                            value={formatNumber(data.current.claimsProcessedCount)}
                            infoHeader="# Claims processed"
                            infoBody="Total claims processed during this period. Includes rejected claims."
                            errorMsg={data.current.errors?.claims}
                        />
                        <MetricTile
                            label="Total Processed Claims Amount"
                            value={formatCurrencyShort(data.current.claimsProcessedAmount)}
                            infoHeader="Total Processed Claims Amount"
                            infoBody="Total dollar amount processed on claims processed during this period."
                            errorMsg={data.current.errors?.claims}
                        />
                        <MetricTile
                            label="% Funded w/in 30 days"
                            value={formatPercent(data.current.fundedWithin30Days.percent)}
                            sub={`${formatNumber(data.current.fundedWithin30Days.numerator)} / ${formatNumber(data.current.fundedWithin30Days.denominator)}`}
                            tone={data.current.fundedWithin30Days.percent != null && data.current.fundedWithin30Days.percent >= 95 ? "positive" : "default"}
                            infoHeader="% Funded within 30 days"
                            infoBody="Of the claims that need to be funded, how many were funded within 30 days of being processed."
                            errorMsg={data.current.errors?.fundedWithin30Days}
                        />
                        <MetricTile
                            label="% SLA ≤3 business days"
                            value={formatPercent(data.current.slaWithin3BusinessDays.percent)}
                            sub={`${formatNumber(data.current.slaWithin3BusinessDays.numerator)} / ${formatNumber(data.current.slaWithin3BusinessDays.denominator)}`}
                            tone={data.current.slaWithin3BusinessDays.percent != null && data.current.slaWithin3BusinessDays.percent >= 90 ? "positive" : "default"}
                            infoHeader="% SLA ≤3 business days"
                            infoBody="How quickly our team turns around claims, measured from the date we received a claim to when it's processed. The period selects claims received in the date range; claims not yet processed are excluded. Matches the Adjudication SLA report."
                            errorMsg={data.current.errors?.slaWithin3BusinessDays}
                        />
                    </div>
                )}

                {this.state.contestAdvisors.length > 0 && (
                    <div style={{ background: "white", border: "1px solid #e5e5e5", borderRadius: 8, padding: 16, marginTop: 24 }}>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <span style={{ fontWeight: 600 }}>Contest Codes — Advisors &amp; Commissions</span>
                            <CustomToolTip
                                header="Contest Codes — Advisors & Commissions"
                                bodyContent="Year-to-date totals for all active contest-code policies. This table isn't affected by the date range or the NHC / JG toggle above."
                            />
                        </div>
                        <div className="text-muted small mb-3">Year-to-date · not affected by the date range or NHC / JG toggle above.</div>
                        <table className="table table-striped table-hover mb-0">
                            <thead>
                            <tr>
                                <th>Primary Advisor</th>
                                <th>Province</th>
                                <th># Contest Policies</th>
                                <th>
                                    Commission (YTD)
                                    <CustomToolTip
                                        header="Commission (YTD)"
                                        bodyContent="Commission this advisor has personally earned so far this year on their own contest-code policies. Override or split portions paid to other advisors on those policies aren't included."
                                    />
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {this.state.contestAdvisors.map((row: any, index: number) => (
                                <tr key={index}>
                                    <td>{row.primaryAdvisor}</td>
                                    <td>{row.province}</td>
                                    <td>{row.contestPolicyCount}</td>
                                    <td>{(row.commissionYtd ?? 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" })}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }
}

function toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

interface MetricTileProps {
    label: string;
    value: string;
    sub?: string;
    tone?: "default" | "positive" | "negative";
    errorMsg?: string;
    infoHeader?: string;
    infoBody?: React.ReactNode;
}

function MetricTile({ label, value, sub, tone = "default", errorMsg, infoHeader, infoBody }: MetricTileProps) {
    const color = tone === "positive" ? "#198754" : tone === "negative" ? "#dc3545" : "inherit";
    return (
        <div style={{ background: "white", border: "1px solid #e5e5e5", borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {label}
                {infoHeader && infoBody && (
                    <CustomToolTip header={infoHeader} bodyContent={infoBody} />
                )}
                {errorMsg && (
                    <CustomToolTip header="Warning" bodyContent={errorMsg} />
                )}
            </div>
            <div style={{ fontSize: 30, fontWeight: 600, marginTop: 4, color }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

function formatNumber(n: number): string {
    return n.toLocaleString();
}

function formatCurrencyShort(n: number): string {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
    return `$${n.toFixed(0)}`;
}

function formatPercent(p: number | null): string {
    return p == null ? "—" : `${p.toFixed(1)}%`;
}
